import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./index.module.css";
import { useEventOverride } from "./useEventOverride";
import NoteModalCloseIcon from "./NoteModalCloseIcon";

function getEditedByText(overrideNotes) {
  if (!overrideNotes) {
    return "N/A";
  }

  const value = String(overrideNotes);
  const match = value.match(/Added\s+By\s+(.+?)\s+on\s+([0-9/]+)/i);
  if (!match) {
    return "N/A";
  }

  const editedByUser = (match[1] || "").trim();
  const editedByDate = (match[2] || "").trim();
  if (!editedByUser || !editedByDate) {
    return "N/A";
  }

  return `${editedByUser} - ${editedByDate}`;
}

function getNoteModalContent(overrideNotes) {
  if (!overrideNotes) {
    return {
      dateTime: "N/A",
      editedBy: "N/A",
      noteText: "N/A",
    };
  }

  const value = String(overrideNotes).trim();
  const match = value.match(
    /Added\s+By\s+(.+?)\s+on\s+([0-9/:\-\sA-Za-z\.]+?)(?:\s*-\s*(.*))?$/i
  );

  if (!match) {
    return {
      dateTime: "N/A",
      editedBy: "N/A",
      noteText: value,
    };
  }

  const editedBy = (match[1] || "").trim() || "N/A";
  const dateTime = (match[2] || "").trim() || "N/A";
  const noteText = (match[3] || "").trim() || "N/A";

  return {
    dateTime,
    editedBy,
    noteText,
  };
}

export default function ThePulse({ iboNumber }) {
  const [activeNote, setActiveNote] = useState("");

  const {
    yearOptions,
    filtersLoading,
    groupsLoading,
    selectedYear,
    selectedEventType,
    selectedEventName,
    selectedGroupId,
    displayBadge,
    noteValue,
    eventTypes,
    eventNames,
    groupsForSelection,
    groups,
    setSelectedYear,
    setSelectedEventType,
    setSelectedEventName,
    setSelectedGroupId,
    setDisplayBadge,
    setNoteValue,
    handleAction,
    getStatusClass,
  } = useEventOverride(iboNumber);
  const activeNoteDetails = useMemo(
    () => getNoteModalContent(activeNote),
    [activeNote]
  );
  const canUsePortal = typeof document !== "undefined";

  useEffect(() => {
    if (!activeNote) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveNote("");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeNote]);
  const isAddToGroupReady = Boolean(
    selectedYear &&
      selectedEventType &&
      selectedEventName &&
      selectedGroupId &&
      noteValue.trim()
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(null);

  return (
    <div className={styles.outer}>
      <div className={styles.container}>
        <div className={styles.contentSection}>
          <div className={styles.sectionTitle}>Event Override</div>
          <div className={styles.filterRow}>
            <div className={styles.filterField}>
              <label className={styles.filterLabel} htmlFor="pulseYear">
                Year
              </label>
              <select
                id="pulseYear"
                className={styles.filterSelect}
                value={selectedYear}
                disabled={filtersLoading}
                onChange={(event) => {
                  setSelectedYear(Number(event.target.value));
                }}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            {eventTypes.length > 0 && (
              <div className={styles.filterField}>
                <label className={styles.filterLabel} htmlFor="pulseEventType">
                  Event Type
                </label>
                <select
                  id="pulseEventType"
                  className={styles.filterSelect}
                  value={selectedEventType}
                  onChange={(event) => {
                    setSelectedEventType(event.target.value);
                    setSelectedEventName("");
                    setSelectedGroupId("");
                  }}
                >
                  <option value="">Select Event Type</option>
                  {eventTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {selectedEventType && eventNames.length > 0 && (
              <div className={styles.filterField}>
                <label className={styles.filterLabel} htmlFor="pulseEventName">
                  Event Name
                </label>
                <select
                  id="pulseEventName"
                  className={styles.filterSelect}
                  value={selectedEventName}
                  onChange={(event) => {
                    setSelectedEventName(event.target.value);
                    setSelectedGroupId("");
                  }}
                >
                  <option value="">Select Event Name</option>
                  {eventNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {selectedEventName && groupsForSelection.length > 0 && (
              <div className={styles.filterField}>
                <label className={styles.filterLabel} htmlFor="pulseGroup">
                  Group
                </label>
                <select
                  id="pulseGroup"
                  className={styles.filterSelect}
                  value={selectedGroupId}
                  onChange={(event) => {
                    setSelectedGroupId(event.target.value);
                  }}
                >
                  <option value="">Select Group</option>
                  {groupsForSelection.map((group) => (
                    <option key={group.groupID} value={group.groupID}>
                      {group.groupName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* <label className={styles.displayBadgeField} htmlFor="pulseDisplayBadge">
              <input
                id="pulseDisplayBadge"
                type="checkbox"
                className={styles.displayBadgeCheckbox}
                checked={displayBadge}
                onChange={(event) => setDisplayBadge(event.target.checked)}
              />
              <span className={styles.displayBadgeText}>Display Badge</span>
            </label> */}
          </div>
          <div className={styles.noteWrapper}>
            <div className={styles.noteRow}>
              <div className={styles.noteLabel}>
                Note <span className={styles.noteRequired}>(Required)</span>
              </div>
            </div>
            <div className={styles.noteInputRow}>
              <input
                className={styles.noteInput}
                type="text"
                placeholder="Add Note"
                aria-label="Add Note"
                value={noteValue}
                onChange={(event) => setNoteValue(event.target.value)}
              />

              <button
                type="button"
                className={styles.addToGroupButton}
                onClick={async () => {
                  if (!isAddToGroupReady || isSubmitting) return;

                  setIsSubmitting(true);
                  try {
                    await handleAction("add");
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={!isAddToGroupReady || isSubmitting} // disabled on page load if filters not selected
              >
                {isSubmitting ? "Adding..." : "Add to Group"}
              </button>
            </div>
          </div>
        </div>
        <div className={styles.dividerWrapper}>
          <div className={styles.divider} />
        </div>
        <div className={styles.includedGroupsText}>
          <span className={styles.includedGroupsTitle}>Included Groups</span>{" "}
          <span className={styles.includedGroupsBody}>
            (Revoke = permanently removes them from the group. Remove = remove
            from group, but if they qualify they will automatically be added
            back).
          </span>
        </div>
        <div className={styles.dividerWrapper}>
          <div className={styles.divider} />
        </div>
        <div className={styles.groupsHeaderRow}>
          <div className={styles.groupNameHeader}>Group Name</div>
          <div className={styles.groupTypeHeader}>GroupType</div>
          <div className={styles.statusHeader}>Status</div>
          {/* <div className={styles.badgeHeader}>Badge</div> */}
          <div className={styles.editedByHeader}>Edited By</div>
          <div className={styles.noteHeader}>Note</div>
          <div className={styles.actionsHeader}></div>
        </div>
        <div className={styles.groupsTable}>
          {groupsLoading && (
            <div className={styles.groupRow}>Loading groups...</div>
          )}
          {!groupsLoading && groups.length === 0 && (
            <div className={styles.groupRow}>No groups found.</div>
          )}
          {!groupsLoading &&
            groups.map((group, index) => {
              const statusText = group.overrideType || group.statusInGroup;
              const statusClass = styles[getStatusClass(group.statusInGroup)];
              const isHomesiteGroup = (group.groupType || "")
                .toLowerCase()
                .includes("homesite");
              const allowRevoke =
                Number(group.allowRevoke) === 1 && !isHomesiteGroup;
              const allowRemove = Number(group.allowRemove) === 1;
              const allowRemoveOverride =
                Number(group.allowRemoveOverride) === 1;
              const editedByText = getEditedByText(group.overrideNotes);

              return (
                <div
                  key={`${group.groupID}-${index}`}
                  className={`${styles.groupRow} ${
                    index % 2 === 1 ? styles.groupRowAlt : ""
                  }`}
                >
                  <div className={styles.groupName}>{group.groupName}</div>
                  <div className={styles.groupType}>{group.groupType}</div>
                  <div className={statusClass}>{statusText}</div>
                  {/* <div className={styles.groupBadge}>N/A</div> */}
                  <div className={styles.groupEditedBy}>{editedByText}</div>
                  <div className={styles.groupNote}>
                    {group.overrideNotes ? (
                      <button
                        type="button"
                        className={styles.viewNoteButton}
                        onClick={() => setActiveNote(group.overrideNotes)}
                      >
                        View
                      </button>
                    ) : null}
                  </div>

                  <div className={styles.groupActions}>
                    {allowRevoke && (
                      <button
                        type="button"
                        className={styles.actionRevoke}
                        onClick={async () => {
                          if (submittingAction) return;
                          setSubmittingAction({
                            groupId: group.groupID,
                            action: "revoke",
                          });
                          try {
                            await handleAction("revoke", group.groupID);
                          } finally {
                            setSubmittingAction(null);
                          }
                        }}
                        disabled={Boolean(submittingAction)}
                      >
                        {submittingAction?.groupId === group.groupID &&
                        submittingAction?.action === "revoke"
                          ? "Processing..."
                          : "Revoke"}
                      </button>
                    )}

                    {allowRemove && (
                      <button
                        type="button"
                        className={styles.actionRemove}
                        onClick={async () => {
                          if (submittingAction) return;
                          setSubmittingAction({
                            groupId: group.groupID,
                            action: "remove",
                          });
                          try {
                            await handleAction("remove", group.groupID);
                          } finally {
                            setSubmittingAction(null);
                          }
                        }}
                        disabled={Boolean(submittingAction)}
                      >
                        {submittingAction?.groupId === group.groupID &&
                        submittingAction?.action === "remove"
                          ? "Processing..."
                          : "Remove"}
                      </button>
                    )}

                    {allowRemoveOverride && (
                      <button
                        type="button"
                        className={styles.actionReinstate}
                        onClick={async () => {
                          if (submittingAction) return;
                          setSubmittingAction({
                            groupId: group.groupID,
                            action: "removeOverride",
                          });
                          try {
                            await handleAction("remove", group.groupID);
                          } finally {
                            setSubmittingAction(null);
                          }
                        }}
                        disabled={Boolean(submittingAction)}
                      >
                        {submittingAction?.groupId === group.groupID &&
                        submittingAction?.action === "removeOverride"
                          ? "Processing..."
                          : "Remove Override"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      {activeNote &&
        canUsePortal &&
        createPortal(
          <div
            className={styles.noteModalOverlay}
            onClick={() => setActiveNote("")}
            role="presentation"
          >
            <div
              className={styles.noteModal}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Note"
            >
              <div className={styles.noteModalHeader}>
                <div className={styles.noteModalTitle}>Note</div>
                <button
                  type="button"
                  className={styles.noteModalClose}
                  onClick={() => setActiveNote("")}
                  aria-label="Close note"
                >
                  <NoteModalCloseIcon />
                </button>
              </div>
              <div className={styles.noteModalBody}>
                <div className={styles.noteModalContent}>
                  <div className={styles.noteModalDateTime}>
                    {activeNoteDetails.dateTime}
                  </div>
                  <div className={styles.noteModalDivider} />
                  <div className={styles.noteModalEditedBy}>
                    Added by {activeNoteDetails.editedBy}
                  </div>
                  <div className={styles.noteModalText}>
                    {activeNoteDetails.noteText}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
